// Fixed, explicitly simulated commercial journey used to demonstrate how a
// Lodge Data signal becomes a campaign brief and a performance report. These
// values are never presented as observed or predicted results.

export const isMauritiusDestination = (org = {}) => {
  const values = [org.country, org.geographic_entity, org.destination]
    .filter(Boolean)
    .map((value) => String(value).trim().toLowerCase());
  return values.some((value) => value === "mauritius" || value === "maurice");
};

// The Mauritius demonstration must open on the real France -> Mauritius ADI
// market. Returning null while the live market catalogue is still loading
// prevents Dashboard from prematurely locking itself on the generic fallback.
export const preferredMarketKey = (org = {}, markets = []) => {
  if (!isMauritiusDestination(org)) return "FR-WORLD";
  return markets.some((market) => market?.value === "FR-MRU") ? "FR-MRU" : null;
};

export const MAURITIUS_DEMO_CAMPAIGN = {
  id: "mauritius-demo-campaign",
  simulation: true,
  objective: "Soutenir les réservations France → Maurice avant la basse saison",
  market: "France",
  period: "Mai–Juin",
  target_audience: "Voyageurs affinitaires 30–55 ans recherchant un séjour premium au soleil",
  budget: { amount: 25000, currency: "EUR" },
  channels: ["Paid social", "Search", "Partenaires voyage"],
  kpis: [
    { name: "Réservations incrémentales", target: 520, current: 0 },
    { name: "Revenu attribué", target: 182000, current: 0 },
  ],
};

export const MAURITIUS_DEMO_REPORT = {
  id: "mauritius-demo-report",
  simulation: true,
  campaign: MAURITIUS_DEMO_CAMPAIGN.objective,
  period: MAURITIUS_DEMO_CAMPAIGN.period,
  metrics: [
    { key: "investment", label: "Investissement", value: "25 000 €" },
    { key: "results", label: "Réservations", value: "520" },
    { key: "revenue", label: "Revenu attribué", value: "182 000 €" },
    { key: "conversion", label: "Conversion", value: "2,9 %" },
    { key: "roi", label: "ROI", value: "+628 %" },
  ],
};

// Presentation-only fixtures used when an immutable Mauritius demo workspace
// has no page-specific payload. They never replace real API data and are
// always surfaced as illustrative simulations in the UI.
export const MAURITIUS_DEMO_MARKETS = [
  { key: "FR", iso: "FR", name: "France", volume: 338000, evolution: -19.4, part_destination: 24, air_passengers: 416000, spend: 1523, length_of_stay: 10.8, lodge_score: 78, source_layer: "DEMO", sources: { volume: "DEMO", air: "DEMO", spend: "DEMO", length_of_stay: "DEMO" }, volume_metric: "Arrivées visiteurs" },
  { key: "RE", iso: "RE", name: "Réunion", volume: 142000, evolution: 4.8, part_destination: 10, air_passengers: null, spend: 980, length_of_stay: 8.2, lodge_score: 66, source_layer: "DEMO", sources: { volume: "DEMO", spend: "DEMO", length_of_stay: "DEMO" }, volume_metric: "Arrivées visiteurs" },
  { key: "GB", iso: "GB", name: "Royaume-Uni", volume: 140000, evolution: -19.7, part_destination: 10, air_passengers: null, spend: 1680, length_of_stay: 11.6, lodge_score: 61, source_layer: "DEMO", sources: { volume: "DEMO", spend: "DEMO", length_of_stay: "DEMO" }, volume_metric: "Arrivées visiteurs" },
  { key: "DE", iso: "DE", name: "Allemagne", volume: 126000, evolution: -15.7, part_destination: 9, air_passengers: null, spend: 1590, length_of_stay: 12.1, lodge_score: 59, source_layer: "DEMO", sources: { volume: "DEMO", spend: "DEMO", length_of_stay: "DEMO" }, volume_metric: "Arrivées visiteurs" },
  { key: "ZA", iso: "ZA", name: "Afrique du Sud", volume: 112000, evolution: -13.8, part_destination: 8, air_passengers: null, spend: 1210, length_of_stay: 7.9, lodge_score: 57, source_layer: "DEMO", sources: { volume: "DEMO", spend: "DEMO", length_of_stay: "DEMO" }, volume_metric: "Arrivées visiteurs" },
  { key: "IN", iso: "IN", name: "Inde", volume: 85000, evolution: 28.7, part_destination: 6, air_passengers: null, spend: 1130, length_of_stay: 9.4, lodge_score: 74, source_layer: "DEMO", sources: { volume: "DEMO", spend: "DEMO", length_of_stay: "DEMO" }, volume_metric: "Arrivées visiteurs" },
];

// Complete only missing market-level fields in the immutable demo. Real CLIENT/LODGE
// values always win, and every injected value keeps an explicit DEMO provenance.
export const enrichMauritiusDemoMarketsPayload = (payload = {}) => {
  const rows = payload?.markets || [];
  if (!payload?.has_data || !rows.length) return payload;
  let enriched = false;
  const markets = rows.map((row) => {
    const fixture = MAURITIUS_DEMO_MARKETS.find((item) =>
      [item.key, item.iso].includes(row?.key) || [item.key, item.iso].includes(row?.iso));
    if (!fixture) return row;
    const next = { ...row, sources: { ...(row.sources || {}) } };
    ["air_passengers", "spend", "length_of_stay"].forEach((field) => {
      if (next[field] == null && fixture[field] != null) {
        next[field] = fixture[field];
        const sourceKey = field === "air_passengers" ? "air" : field;
        next.sources[sourceKey] = "DEMO";
        enriched = true;
      }
    });
    return next;
  });
  return enriched ? { ...payload, markets, demo_enriched_fields: true } : payload;
};

export const MAURITIUS_DEMO_MARKETS_PAYLOAD = {
  has_data: true,
  is_demo: true,
  simulation: true,
  source_type: "demo",
  source: "Simulation illustrative Lodge & Co",
  role: "DEMO",
  filters: {
    destinations: [{ value: "MU", label: "Maurice" }],
    periods: [{ value: "2025-08_2026-07", label: "Août 2025 – Juillet 2026" }],
    source_markets: MAURITIUS_DEMO_MARKETS.map(({ key, name }) => ({ value: key, label: name })),
  },
  markets: MAURITIUS_DEMO_MARKETS,
};

export const MAURITIUS_DEMO_PARTNERS = [
  { id: "demo-airline", name: "Compagnie aérienne — exemple", type: "AIRLINE", country: "Maurice", relationship_status: "none", simulation: true },
  { id: "demo-hotel", name: "Groupe hôtelier — exemple", type: "HOTEL_GROUP", country: "Maurice", relationship_status: "none", simulation: true },
  { id: "demo-dmc", name: "DMC réceptif — exemple", type: "DMC", country: "Maurice", relationship_status: "none", simulation: true },
  { id: "demo-agency", name: "Tour-opérateur France — exemple", type: "TOUR_OPERATOR", country: "France", relationship_status: "none", simulation: true },
];

export const MAURITIUS_DEMO_ASK_SUGGESTIONS = [
  "Quels marchés faut-il prioriser pour Maurice ?",
  "Comment soutenir la basse saison ?",
  "Quel rôle joue l’aérien France → Maurice ?",
];

export const mauritiusDemoAnswer = (question = "") => {
  const q = String(question).toLowerCase();
  if (q.includes("aérien") || q.includes("air") || q.includes("france")) {
    return "**DATA** : Dans le scénario de démonstration, Air Mauritius représente 36,8 % des passagers et 40,2 % des revenus sur France → Maurice. Le marché France pèse environ 24 % des arrivées.\n\n**ANALYSIS** : La connectivité et la conversion du marché français sont structurantes, mais le recul récent illustré invite à sécuriser la demande plutôt qu’à dépendre uniquement du volume acquis.\n\n**RECOMMENDATION** : Préparer une campagne coopérée avec les partenaires aériens et de distribution, ciblée avant la basse saison, avec réservations incrémentales et revenu attribué comme KPI.";
  }
  if (q.includes("saison") || q.includes("juin") || q.includes("occupation")) {
    return "**DATA** : La démonstration présente un pic de 161 440 arrivées en décembre, contre 89 098 en juin. L’occupation chambres moyenne est de 73,2 %, avec un point bas illustré à 62 % en juin.\n\n**ANALYSIS** : Juin combine une demande inférieure à la moyenne et une capacité hôtelière moins occupée : c’est la fenêtre prioritaire pour une activation anticipée.\n\n**RECOMMENDATION** : Lancer l’acquisition entre mars et mai pour des séjours en juin, puis mesurer réservations, coût d’acquisition, revenu attribué et occupation incrémentale.";
  }
  return "**DATA** : Dans la démonstration Maurice, la France reste le premier marché avec environ 24 % des arrivées mais recule de 19,4 %. L’Inde représente un volume plus faible et progresse de 28,7 %.\n\n**ANALYSIS** : Le portefeuille doit combiner reconquête du marché français, qui reste déterminant, et test contrôlé d’un marché en croissance comme l’Inde.\n\n**RECOMMENDATION** : Affecter le budget principal à la reconquête France et réserver une enveloppe de test à l’Inde, avec des objectifs distincts de revenu, réservations et coût d’acquisition.";
};

export const mauritiusDemoAiResult = (context = {}, actionType = "analyze_market") => {
  const market = MAURITIUS_DEMO_MARKETS.find((item) => item.key === context.marketKey);
  const name = market?.name || context.market || "ce marché";
  const evolution = market?.evolution;
  const share = market?.part_destination;
  const growing = evolution != null && evolution >= 10;
  const declining = evolution != null && evolution <= -10;
  const signal = evolution == null
    ? `${name} : évolution non disponible dans le scénario.`
    : `${name} : ${evolution > 0 ? "+" : ""}${evolution} % sur les six derniers mois vs les six précédents.`;
  const shareSignal = share == null
    ? "Part dans les arrivées non disponible."
    : `${name} représente environ ${share} % des arrivées de la destination.`;

  return {
    summary: actionType === "explain_opportunity"
      ? `Le signal concernant ${name} mérite une action ciblée, mesurée et réversible dans le cadre de la démonstration Maurice.`
      : `L’analyse de démonstration positionne ${name} à partir des arrivées, de leur évolution et des données aériennes disponibles.`,
    keySignals: [signal, shareSignal],
    interpretation: [
      growing
        ? "La progression observée dans le scénario justifie un test d’acquisition, sans extrapoler un potentiel futur."
        : declining
          ? "Le recul observé appelle une stratégie de reconquête plutôt qu’une simple augmentation de volume."
          : "Le marché doit être suivi avec des objectifs limités tant que les signaux restent modérés.",
    ],
    recommendedActions: [
      growing
        ? `Tester une campagne ciblée sur ${name} avec un budget plafonné et des KPI de réservation.`
        : `Construire une action de conversion ou de reconquête sur ${name}, puis mesurer le revenu attribué.`,
      "Comparer les résultats à une période témoin avant toute généralisation.",
    ],
    caveats: [
      "Analyse déterministe de démonstration : elle ne constitue ni une prédiction ni une recommandation client définitive.",
      market?.air_passengers ? "Les données aériennes sont agrégées sur la période affichée." : "Aucune donnée aérienne n’est utilisée pour ce marché.",
    ],
    provenance: ["Données de démonstration Lodge & Co — Maurice"],
    confidence: market ? "Medium" : "Low",
    isDemo: true,
  };
};

export const mauritiusDemoMarketDetail = (marketKey) => {
  const routeOrigin = String(marketKey || "").split("-")[0];
  const market = MAURITIUS_DEMO_MARKETS.find((item) => item.key === marketKey || item.key === routeOrigin);
  if (!market) return null;
  return {
    is_demo: true,
    simulation: true,
    market: {
      ...market,
      seasonality: [94, 88, 111, 103, 128, 105, 91, 96, 97, 96, 82, 110],
    },
    air: market.key === "FR" ? {
      passengers: 416000,
      revenue: 192700000,
      avg_fare: 462.94,
      top_airlines: [
        { rank: 1, name: "Air Mauritius Ltd", passengers: 153000 },
        { rank: 2, name: "Corsair International", passengers: 99000 },
        { rank: 3, name: "Air France", passengers: 62000 },
      ],
    } : null,
    observations: [
      `${market.name} représente ${market.part_destination} % des arrivées dans cette simulation illustrative.`,
      `Évolution illustrée : ${market.evolution > 0 ? "+" : ""}${market.evolution} % sur les six derniers mois vs les six précédents.`,
    ],
    recommendation: market.evolution >= 10
      ? `Tester une campagne d’acquisition ciblée sur ${market.name} avec un budget et des KPI contrôlés.`
      : `Consolider la demande sur ${market.name} et suivre la conversion avant la basse saison.`,
    signals: [{ type: "Signal de démonstration", detail: "Illustration déterministe — ne constitue ni une observation additionnelle ni une prédiction." }],
  };
};

export const toOpportunitiesPageItem = (opportunity) => ({
  id: `lodge-${opportunity.id}`,
  type: opportunity.type,
  opportunity: opportunity.opportunity,
  market: opportunity.market,
  period: "6 derniers mois vs 6 précédents",
  evidence: opportunity.evidence,
  data_used: [opportunity.source || "Lodge Data"],
  potential: opportunity.confidence || "Low",
  recommended_action: opportunity.recommendedAction,
  source: opportunity.source || "Lodge Data",
  marketKey: opportunity.marketKey,
});
